import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconReceipt2, IconArrowBackUp } from '@tabler/icons-react';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { proposeNewQuoteAmount } from '../services/BookingService';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { IQuoteDraftLine } from '../interfaces/heavyDutyMachinery/IQuoteDraft';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { latestQuoteRevision } from '../helpers/quoteHelper';
import {
  IQuoteDraftEditLine,
  draftLineToEditLine,
  editLinesTotal,
  mergeDraftedLines,
  toEditLine,
  toProposedLines,
} from '../helpers/quoteDraftHelper';
import QuoteLineEditor from './quotes/QuoteLineEditor';
import QuoteDraftAssistant from './quotes/QuoteDraftAssistant';
import QuoteVoiceEdit from './quotes/QuoteVoiceEdit';

const { TextArea } = Input;
const { Text } = Typography;

interface ProposeNewQuoteAmountModalProps {
  // The whole booking, not just a figure: the editor starts from the
  // current version's own lines, so it needs them.
  booking: IBooking;
  open: boolean;
  onClose: () => void;
  onProposed: (booking: IBooking) => void;
}

/**
 * Host-side "propose a new total for this deferred-booking quote request,
 * with a mandatory reason" -- appends a new BookingQuoteRevision, doesn't
 * change the booking's status (see BookingController.ProposeQuoteAmount's
 * own doc comment: only the host's first response is time-boxed). Same
 * mobile-sheet/desktop-Modal shape as HostCancelBookingModal/RejectQuoteModal.
 *
 * The editor itself and the describe-the-job assistant are both their own
 * components now: this file is the shell, the mandatory reason, and the one
 * rule that belongs to neither of them -- that regenerating a draft replaces
 * the assistant's lines and never the host's own typing.
 */
const ProposeNewQuoteAmountModal: React.FC<ProposeNewQuoteAmountModalProps> = ({ booking, open, onClose, onProposed }) => {
  const { isMobile } = useAppBreakpoint();
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const currency = booking.hostCurrencyCode;

  // The version on the table, split by what this host may touch. Locked lines
  // are shown but never sent: the server re-attaches them from the previous
  // version itself (see the backend's ProposeQuoteAmount), so they can't be
  // repriced even by a crafted request.
  const current = latestQuoteRevision(booking);
  const lockedLines = (current?.lines ?? []).filter((line) => !line.isEditableByHost);
  const editableLines = () => (current?.lines ?? []).filter((line) => line.isEditableByHost).map(toEditLine);

  const [lines, setLines] = useState<IQuoteDraftEditLine[]>(editableLines);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // One step back, and only for a spoken change.
  //
  // A voice edit is the one action here that can rewrite the whole quote from
  // a sentence that might have been misheard -- every other edit touches one
  // field. Somebody who says "quita el transporte" and watches four lines move
  // needs a way out that is not retyping their own quote, and one step is
  // enough: the second thing they do is look at what happened.
  const [undoableLines, setUndoableLines] = useState<IQuoteDraftEditLine[]>();
  const dragControls = useDragControls();

  const lockedTotal = lockedLines.reduce((sum, line) => sum + line.amount, 0);
  const total = lockedTotal + editLinesTotal(lines);
  const hasDraft = lines.some((line) => line.generated);

  /**
   * A fresh draft replaces the assistant's own previous lines and leaves
   * everything the host wrote alone. Regenerating has to be safe to press --
   * silently deleting somebody's typing because they wanted a second opinion
   * on the wording would be unforgivable.
   */
  const handleDrafted = (drafted: IQuoteDraftLine[]) =>
    setLines((previous) => [...previous.filter((line) => !line.generated), ...drafted.map(draftLineToEditLine)]);

  /**
   * A spoken change comes back as the whole quote, so it replaces the lines --
   * see mergeDraftedLines for what is carried across rather than rebuilt.
   */
  const handleVoiceEdit = (drafted: IQuoteDraftLine[]) =>
    setLines((previous) => {
      setUndoableLines(previous);
      return mergeDraftedLines(previous, drafted);
    });

  const handleUndo = () => {
    if (!undoableLines) return;
    setLines(undoableLines);
    setUndoableLines(undefined);
  };

  const handleClose = () => {
    setLines(editableLines());
    setReason('');
    setUndoableLines(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    if (lines.some((line) => !line.title.trim())) {
      message.error(translate(t, TRANSLATIONS.QUOTE_EDITOR_TITLE_REQUIRED));
      return;
    }
    if (total <= 0) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_AMOUNT_REQUIRED));
      return;
    }
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_REASON_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      const updated = await proposeNewQuoteAmount(booking.id, toProposedLines(lines), reason.trim());
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_SUCCESS));
      onProposed(updated);
      handleClose();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const formFields = (
    <>
      <QuoteDraftAssistant onDrafted={handleDrafted} hasDraft={hasDraft} />

      {lines.length > 0 && <QuoteVoiceEdit lines={lines} onApplied={handleVoiceEdit} />}

      {undoableLines && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: '8px 12px',
            marginBottom: 10,
            borderRadius: 12,
            background: token.colorFillQuaternary,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
            {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_APPLIED)}
          </Text>
          <Button size="small" icon={<IconArrowBackUp size={14} />} onClick={handleUndo} style={{ borderRadius: 999 }}>
            {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_UNDO)}
          </Button>
        </div>
      )}

      <QuoteLineEditor lines={lines} onChange={setLines} lockedLines={lockedLines} currency={currency} />

      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_REASON_LABEL)}
      </Text>
      <TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_REASON_PLACEHOLDER)}
        maxLength={1000}
      />
    </>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="propose-quote-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="propose-quote-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: token.colorWarningBg,
                    color: token.colorWarning,
                  }}
                >
                  <IconReceipt2 size={20} />
                </span>
                <Text strong style={{ fontSize: 16 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_MODAL_TITLE)}
                </Text>
              </div>
              <div style={{ marginBottom: 16, maxHeight: '60vh', overflowY: 'auto', overscrollBehavior: 'contain' }}>{formFields}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  block
                  style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
                  onClick={handleClose}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
                </Button>
                <Button block type="primary" loading={submitting} onClick={handleSubmit} style={{ borderRadius: 999 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_CONFIRM_BUTTON)}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_MODAL_TITLE)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_CONFIRM_BUTTON)}
      confirmLoading={submitting}
      // An itemised quote with the assistant on top is taller than a viewport:
      // without this the confirm button walks off the bottom of the screen.
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
    >
      {formFields}
    </Modal>
  );
};

export default ProposeNewQuoteAmountModal;
